import React from 'react';
import { getPtbToken } from '../apiClient';

const PTB_URL = process.env.REACT_APP_PTB_URL;
// Parsed at import time, and this page is imported eagerly, so a malformed value must not throw:
// it would white-screen the whole app, not just this page (review, 2026-09-29).
export function ptbOrigin(url) {
  try { return url ? new URL(url).origin : null; } catch { return null; }
}
const PTB_ORIGIN = ptbOrigin(PTB_URL);

function SolutionTesting() {
  const iframeRef = React.useRef(null);
  const [error, setError] = React.useState(null);

  // Hands PTB its access token via postMessage rather than the iframe's URL — a bearer token in a
  // URL ends up in server access logs, browser history, and any Referer header a subresource might
  // see. PTB pings this window "I'm ready" once it's loaded (see apps/ptb/app/src/authToken.ts);
  // only then do we reply, and only to PTB's own origin, after confirming the ping actually came
  // from the iframe we rendered — never a broadcast, never trusting an unexpected sender.
  React.useEffect(() => {
    if (!PTB_ORIGIN) return;

    function handleMessage(event) {
      if (event.origin !== PTB_ORIGIN) return;
      if (event.source !== iframeRef.current?.contentWindow) return;
      if (event.data?.type !== 'ptb-ready') return;

      getPtbToken()
        .then(token => event.source.postMessage({ type: 'ptb-token', token }, PTB_ORIGIN))
        .catch(err => setError(err.message ?? 'Could not sign in to PTB'));
    }

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (!PTB_ORIGIN) {
    return (
      <div className="placeholder-page">
        <div className="placeholder-icon">✅</div>
        <div className="placeholder-title">Solution Testing & Validation</div>
        <div className="placeholder-sub">
          {PTB_URL
            ? <>Misconfigured: <code>REACT_APP_PTB_URL</code> isn't a valid URL (<code>{PTB_URL}</code>).</>
            : <>Not configured in this environment. Set <code>REACT_APP_PTB_URL</code> to embed Nicole's
              PTB app here.</>}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Solution Testing & Validation</div>
        <div className="page-title">Solution Testing</div>
        <div className="page-subtitle">Data validation and software functionality testing, via PTB.</div>
      </div>
      {error && (
        <div className="placeholder-page">
          <div className="placeholder-title">Couldn't sign in to Solution Testing</div>
          <div className="placeholder-sub">{error}</div>
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="PTB"
        src={PTB_URL}
        className="ptb-embed-frame"
        style={error ? { display: 'none' } : undefined}
      />
    </div>
  );
}

export default SolutionTesting;
